import axios from "axios";
import { API_BASE_URL } from "../config/api";
import type { RFQ, RFQUpdate } from "../types/rfq";
import { apiClient, createClient, SUPABASE_KEY, SUPABASE_URL, supabaseRestClient } from "./http";
import { fetchWithActor } from "./actor";

// Utility functions for price calculations
export const calculateMarkup = (costPrice: number, sellingPrice: number): number => {
  if (costPrice <= 0 || sellingPrice <= 0) return 0;
  return Number(((sellingPrice / costPrice - 1) * 100).toFixed(1));
};

// Helper function to round to nearest dollar - only used for calculated prices
export const roundTo99Cents = (price: number): number => {
  return Math.round(price);
};

// Function to log sent quote items to the database
export const logSentQuoteItems = async (rfq: RFQ, quotePdfUrl: string) => {
  try {
    // Create a simplified payload with just the items that need updating
    const payload = rfq.items.map((item) => ({
      rfq_id: rfq.rfq_id,
      rfq_item_id: item.rfq_item_id,
      quoted_price: item.quoted_price,
      markup_percentage: item.markup_percentage,
      cost_price: item.cost_price,
      price_validated: item.price_validated,
      indent_days: item.indent_days
    }));

    const response = await apiClient.post(`/quotes/quote/${rfq.rfq_id}/log-sent`, {
      items: payload,
      quotePdfUrl,
      senderName: rfq.quote_sent_by || 'System'
    });

    if (!response.data || response.status !== 200) {
      throw new Error("Failed to log sent quote items");
    }

    return { success: true };
  } catch (error) {
    console.error("Error logging sent quote items:", error);
    throw error;
  }
};

/** The rfq_items columns the RFQ list reads (no embeddings, no search_tsv). */
const RFQ_LIST_SELECT = `
        *,
        rfq_items(
          rfq_item_id,
          rfq_id,
          item_name,
          model_number,
          quantity_requested,
          cost_price,
          quoted_price,
          markup_percentage,
          indent_days,
          status,
          price_sources,
          price_validated,
          validation_notes,
          remarks,
          remark_tags,
          validated_by,
          validated_at,
          last_updated,
          item_order,
          original_text,
          error_details,
          order_index,
          saved_remark_id
        )
      `;

const isTimeout = (error: { message?: string; code?: string }, withCode: boolean) =>
  !!error.message?.includes('timeout') ||
  !!error.message?.includes('statement_timeout') ||
  (withCode && error.code === '57014');

/**
 * The list rows as the UI's RFQ: items under `items`, placeholder client
 * fields. The database's nullable columns meet the UI type here, at the one
 * place rows enter the app, exactly as they did when the client was untyped.
 */
const toRfqs = (rows: ReadonlyArray<Record<string, unknown> & { rfq_items?: unknown }> | null): RFQ[] =>
  (rows || []).map((rfq) => ({
    ...rfq,
    client_name: rfq.client_name || "Unknown",
    client_email: rfq.client_email || "unknown@example.com",
    rfq_received_date: rfq.rfq_received_date,
    items: rfq.rfq_items || [],
  })) as unknown as RFQ[];

/** How many RFQs each tab holds; DIY counts only `diyUser`'s. No items fetched. */
export const getRfqStatusCounts = async (diyUser: string): Promise<{ NEW: number; DIY: number; QUOTED: number }> => {
  const { supabase } = await import('../config/supabase');

  const [newCount, diyCount, quotedCount] = await Promise.all([
    supabase.from('rfqs').select('rfq_id', { count: 'exact', head: true }).eq('status', 'NEW'),
    supabase.from('rfqs').select('rfq_id', { count: 'exact', head: true })
      .eq('status', 'DIY')
      .eq('moved_to_manual_by', diyUser),
    supabase.from('rfqs').select('rfq_id', { count: 'exact', head: true }).eq('status', 'QUOTED')
  ]);

  return {
    NEW: newCount.count || 0,
    DIY: diyCount.count || 0,
    QUOTED: quotedCount.count || 0
  };
};

// Update getRFQs to apply auto-pricing for NEW RFQs
export const getRFQs = async (
  pageOrParams: number | { page?: number; status?: string; searchQuery?: string } = {}
): Promise<RFQ[]> => {
  try {
    // Handle different parameter types
    const status = typeof pageOrParams !== "number" ? pageOrParams.status : undefined;
    const searchQuery = typeof pageOrParams !== "number" ? pageOrParams.searchQuery : undefined;

    // Use the Supabase JS client directly instead of raw axios calls
    // This properly handles pagination and row limits
    const { supabase } = await import('../config/supabase');

    // Build the query using Supabase JS client
    // Exclude heavy ML/embedding columns based on actual database schema:
    // ❌ name_spec_embedding (768-dimension vector ~3KB per item)
    // ❌ normalized_model_number (derived field for ML matching)
    // ❌ search_tsv (tsvector for full-text search)
    // ✅ Only include columns that actually exist in the database
    let query = supabase
      .from('rfqs')
      .select(RFQ_LIST_SELECT, { count: 'exact' })
      .order('rfq_id', { ascending: false });

    // Apply status filter if provided - THIS IS THE KEY OPTIMIZATION
    // When filtering by status, we can fetch ALL matching RFQs safely
    // because NEW and DIY are small sets, and QUOTED benefits from the filter
    if (status) {
      query = query.eq('status', status);

      // Apply server-side search if search query is provided
      // This allows searching through ALL RFQs efficiently, not just loaded ones
      if (searchQuery && searchQuery.trim()) {
        // When searching, fetch a larger set (1000-2000 for QUOTED) to search through
        // The search will then filter these on the client side
        // This is more efficient than loading ALL RFQs when not searching
        let searchLimit = 2000;
        if (status === 'QUOTED') {
          // For QUOTED, try to fetch 1500 records for search
          // If timeout, will fallback to fewer records
          searchLimit = 1500;
        }

        const { data: rfqs, error } = await query.range(0, searchLimit - 1);

        if (error) {
          console.error("Supabase JS client error during search fetch:", error);

          // If timeout on search, retry with smaller limit
          if (status === 'QUOTED' && isTimeout(error, true)) {
            const { data: retryRfqs, error: retryError } = await query.range(0, 749);

            if (retryError) {
              console.error("Supabase JS client retry error:", retryError);
              throw new Error(`Supabase search error after retry: ${retryError.message}`);
            }

            return toRfqs(retryRfqs);
          }

          throw new Error(`Supabase search error: ${error.message}`);
        }

        return toRfqs(rfqs);
      }

      // No search query - apply default limits to prevent timeout
      // Different limits for different statuses to prevent timeouts
      // NEW and DIY: Small sets, fetch more (1000)
      // QUOTED: Large set with many items, use smaller limit to prevent timeout (500)
      let limit = 1000; // Default for NEW, DIY
      if (status === 'QUOTED') {
        limit = 500; // Reduced limit for QUOTED to prevent timeout
      }

      const { data: rfqs, error } = await query.range(0, limit - 1);

      if (error) {
        console.error("Supabase JS client error:", error);

        // If timeout on QUOTED, retry with even smaller limit
        if (status === 'QUOTED' && error.message && isTimeout(error, true)) {
          const { data: retryRfqs, error: retryError } = await query.range(0, 249);

          if (retryError) {
            console.error("Supabase JS client retry error:", retryError);
            throw new Error(`Supabase error after retry: ${retryError.message}`);
          }

          return toRfqs(retryRfqs);
        }

        throw new Error(`Supabase error: ${error.message}`);
      }

      return toRfqs(rfqs);
    }

    // When NO status filter (fetching all statuses), use smaller limit to prevent timeout
    const { data: rfqs, error } = await query.range(0, 299);

    if (error) {
      console.error("Supabase JS client error:", error);

      // Check if it's a timeout error
      if (error.message && isTimeout(error, false)) {
        // Retry with even fewer records
        const { data: retryRfqs, error: retryError } = await query.range(0, 149);

        if (retryError) {
          console.error("Supabase JS client retry error:", retryError);
          throw new Error(`Supabase error after retry: ${retryError.message}`);
        }

        // Use the retry results
        return toRfqs(retryRfqs);
      }

      throw new Error(`Supabase error: ${error.message}`);
    }

    return toRfqs(rfqs);

  } catch (error) {
    console.error("Error fetching RFQs from Supabase:", error);
    // Rethrow: an empty array here is indistinguishable from "no RFQs", so a
    // transient failure silently emptied the list and React Query's retry and
    // error handling never ran.
    throw error;
  }
};

export const updateRFQ = async (rfqId: number, data: RFQUpdate) => {
  try {
    // Backend uses PUT
    // The backend answers { message, data: the updated rfqs row }.
    const response = await apiClient.put<{ message: string; data?: Partial<RFQ> }>(`/rfqs/${rfqId}`, data);
    return response.data;
  } catch (error) {
    console.error(`Error updating RFQ ${rfqId}:`, error);
    throw error;
  }
};

export const deleteRFQ = async (rfqId: number) => {
  try {
    // The backend handles the cascading deletion
    const response = await apiClient.delete<{ message: string }>(`/rfqs/${rfqId}`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting RFQ ${rfqId}:`, error);
    throw error;
  }
};

export const getQuotePreview = async (rfqId: number) => {
  try {
    // No separate existence check: the one that stood here sent a Supabase
    // REST path (/rest/v1/rfqs) through apiClient, i.e. to the backend, which
    // has no such route - it 404'd every time and every preview failed. The
    // quote-preview route itself answers 404 for a missing RFQ (handled below).
    //
    // A clean client instance (no interceptors), sent to an absolute API URL
    const client = createClient();

    // Remove any trailing slash from API_BASE_URL
    const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;

    // Make sure we have /api in the path
    const apiPath = baseUrl.includes('/api') ? baseUrl : `${baseUrl}/api`;

    const response = await client.get<{
      pdf_data: string;
      quote_number: string;
      was_updated?: boolean;
    }>(`${apiPath}/rfqs/${rfqId}/quote-preview`);

    return response.data;
  } catch (error) {
    console.error('Error in getQuotePreview:', error);
    
    // Enhance error handling with specific messages
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 404) {
        throw new Error(`RFQ ${rfqId} not found or not accessible`);
      } else if (error.response?.status === 400) {
        throw new Error(`Invalid request for RFQ ${rfqId}. Please check if all required data is present.`);
      } else if (error.response?.status === 403) {
        throw new Error('You do not have permission to access this quote preview');
      } else if (!error.response) {
        throw new Error('Network error. Please check your connection and try again.');
      }
    }
    
    // For unknown errors, throw a generic message
    throw new Error('Failed to generate quote preview. Please try again later.');
  }
};

interface RFQDuplicate {
  priorRfqId: number;
  clientName: string | null;
  clientEmail: string | null;
  quoteSentAt: string;
  sameClient: boolean;
  /** False when the item names match but at least one quantity differs. */
  quantitiesMatch: boolean;
  itemCount: number;
  items: Array<{
    rfq_item_id: number;
    item_name: string;
    quantity_requested: number | null;
    quoted_price: number | null;
    cost_price: number | null;
    markup_percentage: number | null;
    indent_days: number | null;
    remarks: string | null;
  }>;
}

/**
 * Find a prior sent RFQ with the same item names as this one (quantities may
 * differ; `quantitiesMatch` says whether they do). Advisory — any failure just means "no duplicate banner".
 */
export const getRFQDuplicate = async (rfqId: number): Promise<{ duplicate: RFQDuplicate | null }> => {
  try {
    const response = await fetchWithActor(`${API_BASE_URL}/rfqs/${rfqId}/duplicate`);
    if (!response.ok) return { duplicate: null };
    return await response.json();
  } catch (error) {
    console.error('Error fetching RFQ duplicate:', error);
    return { duplicate: null };
  }
};

/**
 * Copy a prior sent duplicate's line pricing onto this RFQ and mark its items
 * validated, so the normal Send Quote flow takes over.
 */
export const applyPreviousQuote = async (
  rfqId: number,
  priorRfqId: number,
  appliedBy?: string
): Promise<{ applied: number; skipped: number }> => {
  const response = await fetchWithActor(`${API_BASE_URL}/rfqs/${rfqId}/apply-previous-quote`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ priorRfqId, appliedBy }),
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.error || `HTTP error! status: ${response.status}`);
  }
  return await response.json();
};

/**
 * Get just the original subject for an RFQ
 */
export const getRFQOriginalSubject = async (rfqId: number | string) => {
  try {
    // Always try direct Supabase first since the API endpoints might not be deployed
    const directUrl = `${SUPABASE_URL}/rest/v1/rfqs?rfq_id=eq.${rfqId}&select=original_subject,original_email_uid`;
    
    try {
      const directResponse = await fetch(directUrl, {
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_KEY,
          'Authorization': `Bearer ${SUPABASE_KEY}`
        }
      });
      
      if (directResponse.ok) {
        const data = await directResponse.json();
        
        if (data && data.length > 0) {
          return { 
            original_subject: data[0].original_subject,
            original_email_uid: data[0].original_email_uid
          };
        }
      }
    } catch (supabaseError) {
      console.error('Error with direct Supabase query:', supabaseError);
    }
    
    // Fall back to API if direct Supabase fails
    
    // Ensure the API base URL has the correct structure with /api prefix
    const baseUrl = API_BASE_URL.endsWith('/api') ? API_BASE_URL : `${API_BASE_URL}/api`;
    const endpointPath = '/rfqs/';
    const emailInfoEndpoint = 'email-info';
    
    // Try email-info endpoint
    const fallbackUrl = `${baseUrl}${endpointPath}${rfqId}/${emailInfoEndpoint}`;
    
    const response = await fetchWithActor(fallbackUrl);
    if (!response.ok) {
      return { 
        original_subject: null,
        original_email_uid: null 
      };
    }
    
    const data = await response.json();
    return { original_subject: data?.original_subject || null };
  } catch (error) {
    console.error('Error fetching RFQ original subject:', error);
    return { original_subject: null };
  }
};

/** The rfqs columns the failed-email card reads. */
interface FailedRfqRow {
  rfq_id: number | null;
  client_name: string | null;
  client_email: string | null;
  status: string | null;
  rfq_received_date: string | null;
  original_subject: string | null;
  moved_to_manual_by: string | null;
  moved_to_manual_at: string | null;
  notes: string | null;
}

/**
 * Get failed RFQ emails that couldn't be processed
 */
export const getFailedRfqEmails = async () => {
  try {
    // Create dedicated Supabase client
    const supabaseClient = supabaseRestClient();
    
    // Query for RFQs that have actual processing failures
    // Only include RFQs with FAILED or ERROR status, not manually moved ones
    const url = '/rest/v1/rfqs?select=rfq_id,client_name,client_email,status,rfq_received_date,original_subject,moved_to_manual_by,moved_to_manual_at,notes&or=(status.eq.FAILED,status.eq.ERROR)&order=rfq_received_date.desc';

    const response = await supabaseClient.get<FailedRfqRow[] | null>(url, {
      headers: {
        apikey: SUPABASE_KEY,
        Authorization: `Bearer ${SUPABASE_KEY}`,
        Prefer: "return=representation"
      },
    });
    
    if (response.status !== 200) {
      console.error("Error fetching failed RFQs:", response.status, response.statusText);
      return [];
    }
    
    const failedRfqs = response.data || [];
    
    // Transform the data to match expected FailedEmail format
    const failedEmails = failedRfqs.map((rfq) => ({
      uid: rfq.rfq_id?.toString() || 'unknown',
      messageId: rfq.rfq_id?.toString() || 'unknown', 
      subject: rfq.original_subject || `RFQ #${rfq.rfq_id}` || 'No Subject',
      from: rfq.client_email || rfq.client_name || 'Unknown Sender',
      date: rfq.rfq_received_date || rfq.moved_to_manual_at || new Date().toISOString()
    }));
    
    return failedEmails;
  } catch (error) {
    console.error('Error fetching failed RFQ emails:', error);
    return []; // Return empty array on error
  }
};

/**
 * Check the status of manual email move for an RFQ (MOVE_TO_MANUAL tasks)
 */
export const checkManualMoveStatus = async (rfqId: number) => {
  try {
    const url = `/email/rfqs/${rfqId}/manual-move-status`;
    const response = await apiClient.get(url);
    return response.data;
  } catch (error) {
    console.error(`Error checking manual move status for RFQ ${rfqId}:`, error);
    throw error;
  }
};
